import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMultipleZones } from '../hooks/useZoneStatus';
import { ZONE_IDS } from '../store/slices/zonesSlice';
import { getZoneDisplayName } from '../utils/zoneUtils';
import { timeCapsuleApi } from '../services/apiService';
import TcButton from './TcButton';
import TcPaper from './TcPaper';

export default function ZoneAdmin() {
    const { t } = useTranslation();
    const [isResetting, setIsResetting] = useState<Record<string, boolean>>({});
    
    const allZoneIds = Object.values(ZONE_IDS);
    const { zonesData, isAnyLoading, hasAnyError, fetchAllZones } = useMultipleZones(allZoneIds);

    const handleResetZone = async (zoneId: string) => {
        setIsResetting(prev => ({ ...prev, [zoneId]: true }));
        try {
            await timeCapsuleApi.updateZoneStatus(zoneId, {
                status: 'available',
                claimedBy: '', // Reset claimedBy to empty string
                payload: {
                    option: 0,
                },
            });
            console.log(`✅ Zone ${zoneId} reset to available`);
            // Refresh the zone status
            setTimeout(() => {
                fetchAllZones();
            }, 1000);
        } catch (error) {
            console.error(`❌ Failed to reset zone ${zoneId}:`, error);
        } finally {
            setIsResetting(prev => ({ ...prev, [zoneId]: false }));
        }
    };

    return (
        <div className="zone-admin p-4 bg-gray-900 rounded-lg">
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-white leading-tight">Zone Status Admin</h2>
                <TcButton
                    handleClick={fetchAllZones}
                    disabled={isAnyLoading}
                    classNameStyle="text-sm py-2 px-4"
                    hollow={true}
                >
                    {isAnyLoading ? 'Refreshing...' : 'Refresh All Zones'}
                </TcButton>
            </div>

            {isAnyLoading && (
                <div className="text-center py-4 text-yellow-300 mb-4">
                    Loading zones...
                </div>
            )}

            <div className="grid gap-4">
                {zonesData.map(({ zoneId, status, isLoading: zoneLoading, error: zoneError }) => (
                    <div key={zoneId} className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="font-medium text-white">{getZoneDisplayName(zoneId)}</h3>
                            <div className="flex items-center gap-2">
                                {zoneLoading && <span className="text-yellow-300 text-sm">Loading...</span>}
                                {status && (
                                    <span className={`text-sm px-3 py-1 rounded-full ${
                                        status.status === 'available' ? 'bg-green-600 text-green-100' :
                                        status.status === 'processing' ? 'bg-blue-600 text-blue-100' :
                                        'bg-red-600 text-red-100'
                                    }`}>
                                        {status.status.toUpperCase()}
                                    </span>
                                )}
                            </div>
                        </div>

                        {zoneError && (
                            <div className="mb-3 p-2 bg-red-900/50 border border-red-500 rounded text-red-300 text-sm">
                                Error: {zoneError}
                            </div>
                        )}

                        {status && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm mb-3">
                                <div className="text-left">
                                    <span className="text-gray-400">Zone ID:</span>
                                    <span className="text-white ml-2">{status.zoneId}</span>
                                </div>
                                <div className="text-left">
                                    <span className="text-gray-400">Last Updated:</span>
                                    <span className="text-white ml-2">
                                        {new Date(status.updatedAt).toLocaleString()}
                                    </span>
                                </div>
                                {status.claimedBy && (
                                    <div className="md:col-span-2 text-left">
                                        <span className="text-gray-400">Claimed By: </span>
                                        <br />
                                        <span className="text-orange-300">{status.claimedBy}</span>
                                    </div>
                                )}
                                {status.payload && (
                                    <div className="md:col-span-2 text-left">
                                        <span className="text-gray-400">Payload:</span>
                                        <pre className="text-white text-xs mt-1 bg-gray-900 p-2 rounded border max-h-32 overflow-auto">
                                            {JSON.stringify(status.payload, null, 2)}
                                        </pre>
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="flex items-center gap-2">
                            <TcButton
                                handleClick={() => handleResetZone(zoneId)}
                                disabled={isResetting[zoneId] || zoneLoading}
                                classNameStyle="text-sm py-1 px-3"
                                hollow={true}
                            >
                                {isResetting[zoneId] ? 'Resetting...' : 'Reset to Available'}
                            </TcButton>
                            
                            {status?.status === 'processing' && (
                                <span className="text-blue-300 text-sm">
                                    ⚡ Zone is actively in use
                                </span>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {hasAnyError && (
                <div className="mt-4 p-3 bg-red-900/50 border border-red-500 rounded-lg">
                    <span className="text-red-300">
                        ⚠️ Some zones failed to load. Check individual zone errors above.
                    </span>
                </div>
            )}

            <div className="mt-6 p-4 bg-gray-800 rounded-lg border border-gray-700">
                <h3 className="font-medium text-white mb-2">Zone Status Legend</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                    <div className="flex items-center gap-2">
                        <span className="w-3 h-3 bg-green-600 rounded-full"></span>
                        <span className="text-green-300">Available - Ready for visitors</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="w-3 h-3 bg-blue-600 rounded-full"></span>
                        <span className="text-blue-300">Processing - Visitor actively using</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="w-3 h-3 bg-red-600 rounded-full"></span>
                        <span className="text-red-300">Error - Needs attention</span>
                    </div>
                </div>
            </div>
        </div>
    );
} 